import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Redirect, useLocation } from 'react-router-dom';
import * as queryString from 'query-string';

import config from '../../../config';
import requireAuth from '../../components/hocs/requireAuth';
import requireOnboarding from '../../components/hocs/requireOnboarding';
import requestLocation from '../../components/hocs/RequestLocation';

import UpgradeToPowerPopUp from '../../components/Upgrade/UpgradeToPowerPopUp';
import { SUBSCRIPTIONS_PATH } from '../../components/hooks/useStartPowerCheckout';
import GenericPopupWithTwoButtons from '../../components/PopUps/GenericPopup/GenericPopupWithTwoButtons';
import head from '../../components/Meta';

import { confirmGetRated } from '../../actions';
import * as actions from '../../actions/dashboard-actions';
import * as searchActions from '../../actions/search-actions';
import * as componentConfigActions from '../../actions/component-config-actions';
import * as topRankedActions from '../../actions/top-ranked-actions';
import { getFeedPreferences } from '../../actions/settings-actions';
import { setPlayerId } from '../../actions/profile-actions';
import DashboardContentV2 from './DashboardContentV2';
import * as onboardingActions from '../../actions/onboarding-actions';
import { showErrorPopup } from '../../actions/notification-actions';

import { useBraze } from '../../components/hooks/useBraze';

function Dashboard(props) {
    const { fetchTopRanked, auth, topRanked, onboarding } = props;
    const [showPowerPopup, setShowPowerPopup] = useState(false);
    const [showPowerDunningPopup, setShowPowerDunningPopup] = useState(false);
    const [redirectAfterPowerUpgrade, setRedirectAfterPowerUpgrade] = useState(null);
    const location = useLocation();
    const { brazeInstance } = useBraze();
    const [isMounted, setIsMounted] = useState(false);

    useEffect(() => {
        const parsed = queryString.parse(location.search);
        const { isPowered } = auth?.user || {};
        const hasTopRanked = topRanked?.men?.players?.length && topRanked?.women?.players?.length;
        const upgradeAndRedirectInNext = !!parsed?.next && parsed?.next.find('upgradeAndRedirect') > 0;

        setIsMounted(true);

        setShowPowerPopup(!isPowered && (upgradeAndRedirectInNext || parsed.upgrade === 'true' || parsed?.upgradeAndRedirect !== undefined || onboarding.showPowerPopup));

        localStorage.removeItem('dashboard-dunning-last-notice');

        // UT-8135: a bit ugly to have this set of if blocks, but we need to account for when a user is not logged in
        // and redirected to main UT with intent to redirect back to Insights, AND scenario in which a user logs in
        // after they hit the Insights app in another tab.
        if (upgradeAndRedirectInNext || !!parsed?.upgradeAndRedirect) {
            const urlRedirect = upgradeAndRedirectInNext ? parsed.next : parsed.upgradeAndRedirect;
            if (!isPowered) {
                setRedirectAfterPowerUpgrade(urlRedirect);
            } else {
                window.open(urlRedirect, '_self');
            }
        }
        if (!hasTopRanked) {
            fetchTopRanked();
        }
        return () => {
            setIsMounted(false);
        };
    }, []);

    useEffect(() => {
        if (brazeInstance) {
            const { id: memberId } = auth?.user || {};

            brazeInstance.automaticallyShowInAppMessages();

            if (memberId) {
                const brazeUser = brazeInstance.getUser();
                const currentBrazeUserId = brazeUser ? parseInt(brazeUser.getUserId(), 10) : null;
                if (currentBrazeUserId !== memberId) {
                    brazeInstance.changeUser(memberId);
                }
            }

            brazeInstance.openSession();
        }
    }, [brazeInstance]);

    const closePowerPopup = () => {
        props.setShowPowerOnboarding(false);
        setShowPowerPopup(false);
    };

    const closePowerDunningPopup = () => {
        setShowPowerDunningPopup(false);
    };

    const handlePowerSuccess = () => {
        setTimeout(() => {
            closePowerPopup();
        }, 1000);
        if (redirectAfterPowerUpgrade != null) {
            props.setShowPowerOnboarding(false);
            window.open(redirectAfterPowerUpgrade, '_self');
        }
    };

    const handlePowerDunningSuccess = () => {
        window.location.href = '/subscriptions';
    };

    if (!isMounted) {
        return null;
    }

    return (
        <>
            {head({
                title: `Home | UTR Sports`,
                keywords: 'UTR, UTR Rating, UTR tennis, UTR Sports Rating, UTR Sports events, UTR ranking, Find UTR Sports events',
                image: `https://www.${config.siteBaseUrl}/img/utr_sports/utr-sports-logo.svg`,
                imageWidth: '930',
                imageHeight: '395',
            })}
            <DashboardContentV2 {...props} />
            {showPowerPopup && auth?.user?.hasPastDueSubscription && (
                <Redirect to={SUBSCRIPTIONS_PATH} />
            )}
            {showPowerPopup && !auth?.user?.hasPastDueSubscription && (
                <UpgradeToPowerPopUp onCloseClick={closePowerPopup} onSuccess={handlePowerSuccess} />
            )}
            {(showPowerDunningPopup) &&
                (
                <GenericPopupWithTwoButtons
                    data-testid="modal.dashboard.payment-issue"
                    isError={true}
                    title={'Payment Issue'}
                    subtitle={(
                        <div className="text-left">
                            <div>Uh oh! Looks like there&apos;s an issue with the latest payment for your Power Player subscription.</div>
                            <div className="mt16">To fix it, head to the <b>Subscriptions tab</b> from your main menu.</div>
                        </div>
                    )}
                    disableClickOutside={true}
                    hideCloseIcon={true}
                    shouldSwapButtonOrder={true}
                    primaryButtonText="Update Now"
                    secondaryButtonText="Remind Me Later"
                    onPrimaryButtonClick={handlePowerDunningSuccess}
                    onSecondaryButtonClick={closePowerDunningPopup}
                />
                )}
        </>
    );
}

function mapDispatchToProps(dispatch) {
    const defaultRankingDisplayCount = 10;
    const defaultRankingTag = 'Pro';
    return {
        confirmGetRated: (data) => {
            dispatch(confirmGetRated(data));
        },
        getMemberDashboard: (user) => {
            dispatch(actions.getMemberDashboard(user));
        },
        disableMemberActionCard: (cardId) => {
            dispatch(actions.disableMemberActionCard(cardId));
        },
        applySearchFilters: (filters, reSearch) => {
            dispatch(searchActions.applySearchFilters(filters, reSearch));
        },
        componentConfigActionPerformed: (configType, actionName) => {
            dispatch(componentConfigActions.postComponentActionPerformd('dashboard', configType, actionName, { properties: {} }));
        },
        clearFilters: (type, reSearch) => {
            dispatch(searchActions.clearFilters(type, reSearch));
        },
        fetchTopRanked: () => {
            dispatch(topRankedActions.fetchTopMenByTags(defaultRankingTag, null, defaultRankingDisplayCount));
            dispatch(topRankedActions.fetchTopWomenByTags(defaultRankingTag, null, defaultRankingDisplayCount));
        },
        getFeedPreferences: () => {
            dispatch(getFeedPreferences());
        },
        getPlayerProfile: (playerId) => {
            dispatch(setPlayerId(playerId, false));
        },
        showErrorPopup: (message, error) => dispatch(showErrorPopup({ message, error })),
        setSkipOnboardingIntroPopup: (bool) => dispatch(onboardingActions.setSkipOnboardingIntroPopup(bool)),
        showOnboardingRangePopup: (bool, type) => dispatch(onboardingActions.showOnboardingRangePopup(bool, type)),
        getRatedPopupDismissed: () => dispatch(onboardingActions.getRatedPopupDismissed()),
        setShowPowerOnboarding: (bool) => dispatch(onboardingActions.setShowPower(bool)),
    };
}

Dashboard.propTypes = {
    auth: PropTypes.instanceOf(Object).isRequired,
    fetchTopRanked: PropTypes.func.isRequired,
    topRanked: PropTypes.shape({
        men: PropTypes.arrayOf(PropTypes.shape),
        women: PropTypes.arrayOf(PropTypes.shape),
    }),
    onboarding: PropTypes.shape({
        showPowerPopup: PropTypes.bool,
    }).isRequired,
};

Dashboard.defaultProps = {
    topRanked: {
        men: [],
        women: [],
    },
};
function mapStateToProps({ auth, dashboard, componentConfig, search, topRanked, onboarding, profile, coreUI }) {
    return { auth, dashboard, componentConfig, search, topRanked, onboarding, profile, coreUI };
}

export default requireAuth(requireOnboarding(requestLocation(connect(mapStateToProps, mapDispatchToProps)(Dashboard))));
